import React, { useEffect, useRef, useState } from "react";
import { FaUserSecret, FaUndo } from "react-icons/fa";
import { toast } from "react-toastify";
import * as impersonation from "../utilities/impersonation";
import { apiEndVendorImpersonation } from "../api";
import usePublishedHeight from "../hooks/usePublishedHeight";

/**
 * ImpersonationBanner
 * ─────────────────────────────────────────────────────────────────────────────
 * Pinned across the top of every dashboard page while a super user is signed
 * in as a vendor, so there is never a moment where the admin could mistake
 * the vendor's dashboard for their own. Shows who they are viewing as, how
 * long the session has left, and the one way out.
 *
 * "Return to admin" ends the vendor session on the server first (so the
 * token is dead even if it leaks), then restores the admin's stashed session
 * and reloads on the directory. Server failure does not trap the admin: the
 * local swap happens regardless, and the session simply expires on its own.
 */
export const IMPERSONATION_BAR_VAR = "--lusuku-impersonation-h";

const styles = {
  bar: {
    position: "sticky",
    top: 0,
    zIndex: 1100,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 16px",
    background: "#4B0082",
    color: "#fff",
    fontSize: "0.82rem",
    fontWeight: 600,
    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
  },
  name: { fontWeight: 800 },
  muted: { opacity: 0.8, fontWeight: 500 },
  button: {
    marginLeft: "auto",
    minHeight: 44,
    padding: "0 14px",
    borderRadius: 7,
    border: "1.5px solid rgba(255,255,255,0.7)",
    background: "rgba(255,255,255,0.12)",
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.78rem",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  },
};

const minutesLeft = (expiresAt) => {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 60000));
};

const ImpersonationBanner = () => {
  const [meta, setMeta] = useState(() => impersonation.active());
  const [leaving, setLeaving] = useState(false);
  const [, tick] = useState(0);
  // Published as --lusuku-impersonation-h so sticky toolbars on the pages
  // below can pin themselves under this bar instead of behind it. Removed
  // again when the banner unmounts, so the pages fall back to top: 0.
  const barRef = useRef(null);
  usePublishedHeight(barRef, IMPERSONATION_BAR_VAR);

  // Re-read once a minute so the countdown moves; cheap, and only while
  // a vendor session is active.
  useEffect(() => {
    if (!meta) return undefined;
    const id = setInterval(() => {
      setMeta(impersonation.active());
      tick((n) => n + 1);
    }, 60000);
    return () => clearInterval(id);
  }, [meta]);

  if (!meta) return null;

  const returnToAdmin = async () => {
    if (leaving) return;
    setLeaving(true);
    try {
      await apiEndVendorImpersonation();
    } catch (_err) {
      // Already expired or unreachable: the local swap below still frees the
      // admin, and the server session dies at its expiry regardless.
    }
    const restored = impersonation.end();
    if (!restored) {
      toast.warn("Your admin session was not found. Please sign in again.");
    }
    window.location.replace(restored ? impersonation.RETURN_ROUTE : "/auth/login-page");
  };

  const left = minutesLeft(meta.expiresAt);

  return (
    <div ref={barRef} className="lsk-impersonation" style={styles.bar} role="status" aria-live="polite">
      <FaUserSecret className="lsk-impersonation__icon" aria-hidden="true" />
      <span className="lsk-impersonation__identity">
        Viewing as <span style={styles.name}>{meta.vendorName || meta.restaurantName || "vendor"}</span>
        {meta.verticalLabel ? <span style={styles.muted}> · {meta.verticalLabel}</span> : null}
        <span className="lsk-impersonation__admin" style={styles.muted}> (Lusuku admin{meta.adminName ? `: ${meta.adminName}` : ""})</span>
      </span>
      {left != null && (
        <span className="lsk-impersonation__countdown" style={styles.muted}>
          {meta.expired ? "Session ended" : `${left} min left`}
        </span>
      )}
      <button type="button" className="lsk-impersonation__return" style={styles.button} onClick={returnToAdmin} disabled={leaving}>
        <FaUndo /> {leaving ? "Returning..." : "Return to admin"}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
